// Server-backed viewport queries for map dots. Replaces the prior
// in-memory bbox iteration over the full NASR dataset.
//
// We call /api/bbox with current viewport bounds + zoom; server returns a
// slimmed slice (typically 50-300 features). Result is cached by viewport
// hash for ~30s to avoid re-querying on every micro-pan.

export type BboxLayer = 'airports' | 'navaids' | 'fixes' | 'heliports' | 'holdings';

// Heliports use the same wire shape as airports (BboxAirport — type
// will always be 'heliport'); they're just sourced from the heliport-
// only filter on the server.

export interface BboxAirport {
  ident: string;
  name: string;
  /** Size bucket: large_airport | medium_airport | small_airport | seaplane_base. */
  type: string;
  lat: number;
  lon: number;
  /** True when the airport has a published ATCT (NASR truth). */
  scheduled: boolean;
  /** True when the airport has an air traffic control tower on the field. */
  hasTower?: boolean;
  /** True when at least one fuel type is reported (NASR truth). */
  hasFuel?: boolean;
  /** Fuel-type code list (e.g. ["100LL", "A"]) when published. */
  fuelTypes?: string[];
  /** Length of the longest paved runway in feet (0 if none). */
  pavedFt?: number;
  /** Whether any runway has edge lighting. */
  lighted?: boolean;
  /** True when the NASR facility name matches the hospital heliport
   *  regex on the server (see api/bbox.ts isHospitalHeliport). Used by
   *  the heliports layer to apply hospital-only filtering / styling
   *  on the client. Always false for non-heliport facility types. */
  hospital?: boolean;
}
export interface BboxNavaid {
  ident: string;
  name: string;
  type: string;
  lat: number;
  lon: number;
  freq: string;
  /** Magnetic variation in degrees east-positive (FAA NASR). */
  magVar?: number | null;
  /** Year the magvar was published. */
  magVarYear?: number | null;
  /** Elevation in feet MSL. */
  elevFt?: number | null;
  /** City + state for human display. */
  city?: string;
  state?: string;
}
export interface BboxFix {
  ident: string; lat: number; lon: number; cat: string; use: string;
}

export interface BboxHolding {
  fixIdent: string;
  patternName: string;
  patternNumber: number;
  fixLat: number;
  fixLon: number;
  inboundCourseDeg: number | null;
  /** Direction of holding on the fix, e.g. 'N', 'NE', 'S'. Useful for
   *  human label rendering. */
  direction: string;
  /** 'L' (non-standard left turns) or 'R' (standard right turns), occasionally blank. */
  turnDirection: string;
  /** Azimuth-type indicator: 'CRS' (course), 'R' (radial), 'RNAV', etc. */
  azimuthType: string;
  /** Raw altitude block text from HPF. */
  altitudes: string;
  /** Outbound leg length raw text from HPF (e.g. '1.0/' for 1 min, '/10' for 10 NM DME). */
  legLength: string;
  /** Optional navaid that provides the bearing/radial for this hold. */
  navaidIdent: string;
  navaidLat: number | null;
  navaidLon: number | null;
}

export type BboxItem<L extends BboxLayer> =
  L extends 'airports'  ? BboxAirport :
  L extends 'heliports' ? BboxAirport :
  L extends 'navaids'   ? BboxNavaid :
  L extends 'holdings'  ? BboxHolding :
  BboxFix;

export interface BboxArgs {
  layer: BboxLayer;
  west: number;
  south: number;
  east: number;
  north: number;
  zoom: number;
  limit?: number;
  signal?: AbortSignal;
  /** Heliports layer only — when true, server returns only facilities
   *  whose name matches the hospital regex. Ignored for other layers. */
  hospitalsOnly?: boolean;
}

// ---------------------------------------------------------------------------
// Canonical bbox URL builders — used by both the runtime and pack.ts
// so cache keys match and the SW can serve pack-populated cache at runtime.
//
// RULE: do not build /api/bbox or bbox=... URLs anywhere else.
//       Route all callers through these helpers to guarantee precision parity.
// ---------------------------------------------------------------------------

/** Build the canonical /api/bbox query string for a layer.
 *  Rounds to 2 decimal places (~1 km) matching the server's cache key. */
export function bboxLayerQs(
  layer: BboxLayer | 'heliports',
  west: number, south: number, east: number, north: number,
  zoom: number,
  extras?: Record<string, string>,
): string {
  const r = (n: number) => n.toFixed(2);
  const params = new URLSearchParams({
    layer,
    w: r(west), s: r(south), e: r(east), n: r(north),
    z: String(Math.round(zoom)),
  });
  if (extras) for (const [k, v] of Object.entries(extras)) params.set(k, v);
  return '/api/bbox?' + params.toString();
}

/** Build a bbox=west,south,east,north param value (4 dp, URL-encoded).
 *  Used for fires/viirs/tfr/sua/obstacles/airspace-class/airways endpoints
 *  which accept a single bbox= param in WSEN order.
 *  4 decimal places matches overlaySpecs.ts bboxWsEn() precision. */
export function bboxWsenParam(
  west: number, south: number, east: number, north: number,
): string {
  const r = (n: number) => n.toFixed(4);
  return `${r(west)},${r(south)},${r(east)},${r(north)}`;
}

export async function fetchBbox<L extends BboxLayer>(args: BboxArgs & { layer: L }): Promise<BboxItem<L>[]> {
  // Use bboxLayerQs so the URL + precision exactly matches what pack.ts
  // stores in cache — ensuring SW cache hits on offline retrieval.
  const extras: Record<string, string> = {};
  if (args.limit) extras['limit'] = String(args.limit);
  if (args.hospitalsOnly && args.layer === 'heliports') extras['hospitalsOnly'] = '1';
  const url = bboxLayerQs(args.layer, args.west, args.south, args.east, args.north, args.zoom, extras);
  const resp = await fetch(url, { signal: args.signal });
  if (!resp.ok) throw new Error('bbox HTTP ' + resp.status);
  const body = (await resp.json()) as { items: BboxItem<L>[] };
  return body.items;
}
